import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Avatar
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { PlatformPostProps } from './types';
import { SocialMediaImage, formatNumber, getInitials } from './SocialMediaPost';

export const YouTubePost: React.FC<PlatformPostProps> = ({ 
  content, 
  sender, 
  displayImageUrl, 
  altText, 
  replies7d, 
  likes7d 
}) => (
  <Paper elevation={0} sx={{ 
    bgcolor: '#ffffff',
    borderRadius: 2,
    overflow: 'hidden'
  }}>
    {/* Video thumbnail with play button */}
    <Box sx={{ position: 'relative' }}>
      {displayImageUrl ? (
        <SocialMediaImage 
          src={displayImageUrl} 
          alt={altText}
          platform="youtube"
        />
      ) : (
        <Box sx={{ 
          width: '100%', 
          height: '180px', 
          bgcolor: '#000'
        }} />
      )}
      
      {/* Overlay play button */}
      <Box sx={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)',
        width: 60,
        height: 60,
        bgcolor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{
          width: 0,
          height: 0,
          borderTop: '15px solid transparent',
          borderBottom: '15px solid transparent',
          borderLeft: '20px solid white',
          ml: 1
        }} />
      </Box>
      
      {/* Video duration */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 10, 
        right: 10,
        bgcolor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        px: 0.5,
        borderRadius: 0.5
      }}>
        <Typography variant="caption">8:24</Typography>
      </Box>
    </Box>
    
    <Box sx={{ p: 2 }}>
      {/* Title */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, lineHeight: 1.2 }}>
        {content.split('\n')[0]}
      </Typography>
      
      {/* Stats */}
      <Box sx={{ display: 'flex', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {formatNumber(Math.round((likes7d || 0) * 20))} views • {Math.floor(Math.random() * 12) + 1} days ago
        </Typography>
      </Box>
      
      {/* Channel info */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar sx={{ width: 36, height: 36, mr: 1 }}>
          {getInitials(sender || '')}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {sender || 'Channel Name'}
        </Typography>
      </Box>
      
      {/* Description (if multi-line content) */}
      {content.includes('\n') && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ 
            whiteSpace: 'pre-wrap',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {content.substring(content.indexOf('\n')+1)}
          </Typography>
        </Box>
      )}
    </Box>
    
  {/* Action bar */}
  <Box sx={{ 
      display: 'flex',
      justifyContent: 'space-between',
      p: 2,
      borderTop: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#222' : '#eee'}`
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ThumbUpOutlinedIcon sx={{ fontSize: 20, mr: 0.5, color: (theme) => theme.palette.mode === 'dark' ? '#aaa' : 'inherit' }} />
        <Typography variant="body2" sx={{ mr: 2, color: (theme) => theme.palette.mode === 'dark' ? '#c7c7c7' : 'inherit' }}>
          {formatNumber(likes7d)}
        </Typography>
        <ThumbUpOutlinedIcon sx={{ fontSize: 20, mr: 0.5, transform: 'rotate(180deg)', color: (theme) => theme.palette.mode === 'dark' ? '#aaa' : 'inherit' }} />
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#c7c7c7' : 'inherit' }}>
          {formatNumber(Math.round((likes7d || 0) * 0.05))}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CommentIcon sx={{ fontSize: 20, mr: 0.5, color: (theme) => theme.palette.mode === 'dark' ? '#aaa' : 'inherit' }} />
        <Typography variant="body2" sx={{ mr: 2, color: (theme) => theme.palette.mode === 'dark' ? '#c7c7c7' : 'inherit' }}>
          {formatNumber(replies7d)}
        </Typography>
        <ShareOutlinedIcon sx={{ fontSize: 20, color: (theme) => theme.palette.mode === 'dark' ? '#aaa' : 'inherit' }} />
      </Box>
    </Box>
  </Paper>
);

export const TikTokPost: React.FC<PlatformPostProps> = ({ 
  content, 
  sender, 
  displayImageUrl, 
  altText, 
  replies7d, 
  likes7d 
}) => (
  <Paper elevation={0} sx={{ 
    bgcolor: '#000000',
    borderRadius: 0,
    color: 'white',
    position: 'relative',
    height: displayImageUrl ? '500px' : '300px',
    overflow: 'hidden'
  }}>
    {/* Main content/video area */}
    {displayImageUrl ? (
      <SocialMediaImage 
        src={displayImageUrl} 
        alt={altText}
        platform="tiktok"
      />
    ) : (
      <Box sx={{ 
        width: '100%', 
        height: '100%', 
        bgcolor: '#121212',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="body1" color="rgba(255,255,255,0.7)">
          Video Preview
        </Typography>
      </Box>
    )}
    
    {/* Overlay elements */}
    <Box sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      p: 2,
      background: 'linear-gradient(transparent, rgba(0,0,0,0.8))'
    }}>
      {/* Username and description */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          @{sender?.replace(/\s+/g, '').toLowerCase() || 'username'} 
          <VerifiedIcon sx={{ fontSize: 14, color: '#20D5EC', ml: 0.5 }} />
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
          {content}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          #fyp #trending {content.split(' ').slice(0, 3).map(word => `#${word.replace(/[^a-zA-Z0-9]/g, '')}`).join(' ')}
        </Typography>
      </Box>
    </Box>
    
    {/* Right side icons */}
    <Box sx={{
      position: 'absolute',
      right: 12,
      bottom: 100,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar 
          sx={{ 
            width: 40, 
            height: 40, 
            border: '2px solid white',
            mb: 0.5
          }}
        >
          {getInitials(sender || '')}
        </Avatar>
        <Box 
          sx={{ 
            width: 18, 
            height: 18, 
            borderRadius: '50%', 
            bgcolor: '#FE2C55',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: 'white',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          +
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FavoriteIcon sx={{ fontSize: 30, mb: 0.5 }} />
        <Typography variant="caption">{formatNumber(likes7d)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ChatBubbleIcon sx={{ fontSize: 30, mb: 0.5 }} />
        <Typography variant="caption">{formatNumber(replies7d)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <BookmarkIcon sx={{ fontSize: 30, mb: 0.5 }} />
        <Typography variant="caption">{formatNumber(Math.round((likes7d || 0) * 0.3))}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ShareIcon sx={{ fontSize: 30, mb: 0.5 }} />
        <Typography variant="caption">{formatNumber(Math.round((likes7d || 0) * 0.2))}</Typography>
      </Box>
    </Box>
    
    {/* Music disc */}
    <Box sx={{
      position: 'absolute',
      bottom: 20,
      right: 80,
      display: 'flex',
      alignItems: 'center',
      bgcolor: 'rgba(0,0,0,0.5)',
      p: 0.5,
      px: 1,
      borderRadius: 2
    }}>
      <MusicNoteIcon sx={{ fontSize: 16, mr: 0.5 }} />
      <Typography variant="caption" sx={{ 
        maxWidth: 120, 
        whiteSpace: 'nowrap', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis'
      }}>
        original sound - {sender?.replace(/\s+/g, '').toLowerCase() || 'username'}
      </Typography>
    </Box>
  </Paper>
);
